import React, { useEffect, useState } from 'react';
import apiClient from '../../api/client';

interface ReactionViewerProps {
  reactionId: number;
  width?: number;
  height?: number;
  className?: string;
}

export const ReactionViewer: React.FC<ReactionViewerProps> = ({
  reactionId,
  width = 600,
  height = 120,
  className = ''
}) => {
  const [svg, setSvg] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchSvg = async () => {
      setLoading(true);
      setError(null);
      try {
        // Request a wide render and omit height so Indigo computes the reaction's
        // natural aspect ratio; we scale it to fit the container below.
        const res = await apiClient.get(`/reactions/${reactionId}/svg`, {
          params: { width },
        });
        if (!cancelled) setSvg(res.data.svg);
      } catch (err) {
        if (!cancelled) {
          setError('Failed to load reaction');
          console.error('Error loading reaction SVG:', err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (reactionId) {
      fetchSvg();
    }
    return () => { cancelled = true; };
  }, [reactionId, width]);

  if (loading) {
    return (
      <div className={`flex items-center justify-center bg-gray-50 rounded ${className}`}
           style={{ width: '100%', height }}>
        <div className="animate-spin h-6 w-6 border-2 border-emerald-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center justify-center bg-red-50 text-red-500 rounded text-sm ${className}`}
           style={{ width: '100%', height }}>
        {error}
      </div>
    );
  }

  // Fill the cell width and let height follow the reaction's natural aspect
  // ratio (height:auto) so the strip isn't letterboxed in a tall fixed box.
  // The grid column uses autoHeight, so the row collapses to the strip height
  // — no dead vertical space.
  const responsiveSvg = svg.replace(
    /<svg /,
    '<svg preserveAspectRatio="xMidYMid meet" style="width:100%;height:auto;display:block" '
  );

  return (
    <div
      className={`reaction-viewer bg-white overflow-hidden ${className}`}
      dangerouslySetInnerHTML={{ __html: responsiveSvg }}
      style={{ width: '100%' }}
    />
  );
};

export default ReactionViewer;
